import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../store';
import { setVideoClipMode } from '../store/mainVideoPlayer';
import { setTimeRange } from '../store/params';
import { getTimeRange, urlWithRange } from '../utils/history';
import { currentVideoSrc } from '../store/translation/selectors';

const useVideoClipMode = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { start, end } = getTimeRange();

  const { startTime: shadowStart, endTime: shadowEnd } = useSelector(
    (state: RootState) => state.params
  );

  const videoSrc = useSelector(currentVideoSrc);

  const { videoClipMode } = useSelector((state: RootState) => state.mainVideoPlayer);

  const prevVideoSrc = useRef(videoSrc);
  const prevStart = useRef(start);
  const prevEnd = useRef(end);
  const isInitialized = useRef(false);

  /**
   * Initialize clip mode from URL params on first load
   */
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      // If URL has start/end params, sync them to state and enable clip mode
      if (start !== undefined && end !== undefined) {
        const startTime = parseFloat(start);
        const endTime = parseFloat(end);

        if (!Number.isNaN(startTime) && !Number.isNaN(endTime)) {
          dispatch(setTimeRange({ startTime, endTime }));
          dispatch(setVideoClipMode(true));
        }
      }
    }
  }, [dispatch, start, end]);

  /**
   * Sync URL params when they change (but not on initial load)
   */
  useEffect(() => {
    if (!isInitialized.current) return;

    // Only process if URL params have actually changed
    if (prevStart.current === start && prevEnd.current === end) {
      return;
    }

    prevStart.current = start;
    prevEnd.current = end;

    // If URL has start/end params but state doesn't, sync state to URL
    if (
      shadowStart === undefined &&
      shadowEnd === undefined &&
      start !== undefined &&
      end !== undefined
    ) {
      const startTime = parseFloat(start);
      const endTime = parseFloat(end);

      if (!Number.isNaN(startTime) && !Number.isNaN(endTime)) {
        dispatch(setTimeRange({ startTime, endTime }));
        dispatch(setVideoClipMode(true));
      }
    }
  }, [dispatch, end, shadowEnd, shadowStart, start]);

  /**
   * Update the URL when videoClipMode or time range changes
   */
  useEffect(() => {
    const showParams = videoClipMode && shadowStart !== undefined && shadowEnd !== undefined;

    history.replace(
      urlWithRange(
        showParams ? shadowStart.toString() : undefined,
        showParams ? shadowEnd.toString() : undefined
      )
    );
  }, [history, shadowEnd, shadowStart, videoClipMode]);

  /**
   * Reset the video clip setting and turn it off when loading a new video
   */
  useEffect(() => {
    if (prevVideoSrc.current === undefined && videoSrc !== undefined) {
      prevVideoSrc.current = videoSrc;
    } else if (prevVideoSrc.current !== videoSrc) {
      prevVideoSrc.current = videoSrc;
      dispatch(setVideoClipMode(false));
      dispatch(setTimeRange({ startTime: undefined, endTime: undefined }));
      history.replace(urlWithRange(undefined, undefined));
    }
  }, [dispatch, history, videoSrc]);
};

export default useVideoClipMode;
